import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'hooks/useTranslation';
import { Button, CollaboratorAvatar, TextInput, LoaderColumn } from 'components';
import { trackingProfileEditStart, trackingProfileEditFinish } from 'services/analytics';
import { pluralize, toTitleCase } from 'helpers/string';
import Icon from 'assets/icons/';
import styles from './ProfileHero.module.scss';
import gql from 'graphql-tag';
import { Logger } from 'services';
import classNames from 'classnames';

/* TODO - #32 Analytics
import {
  eventTrackingNames,
  eventTrackingProperties
} from "../../utils/eventTrackingConstants";
*/
const getProfileHeroQuery = gql`
  query getProfileHero($userId: String!) {
    profileByIdentifier(identifier: $userId) {
      ... on Person {
        name
        email
        avatarUrl
      }
      ... on Profile {
        title
        organization {
          id
          name
        }
        userNodes {
          pageInfo {
            total
          }
        }
        ownsNodes {
          pageInfo {
            total
          }
        }
      }
    }
  }
`;

const updateJobTitleMutation = gql`
  mutation updateJobTitle($jobTitle: String!) {
    updateMe(jobTitle: $jobTitle) {
      ok
      error
    }
  }
`;

interface ProfileHeroProps {
  isCurrentUser: boolean;
  canSeeJobTitle: boolean;
  canEditJobTitle: boolean;
  className?: string;
}

export const ProfileHero: React.FC<ProfileHeroProps> = ({
  isCurrentUser,
  canSeeJobTitle,
  canEditJobTitle,
  className,
}) => {
  const { t } = useTranslation();
  const { userId } = useParams();

  const { error, data } = useQuery(getProfileHeroQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { userId },
  });

  const [updateJobTitle] = useMutation(updateJobTitleMutation);

  const [originalTitle, setOriginalTitle] = useState('');
  const [currentTitle, setCurrentTitle] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (data?.profileByIdentifier?.title) {
      setOriginalTitle(data.profileByIdentifier.title);
      setCurrentTitle(data.profileByIdentifier.title);
    }
  }, [data]);

  if (data === undefined) return <LoaderColumn type="Row" />;

  if (error) return <LoaderColumn type="Row" />;

  const {
    profileByIdentifier: {
      name,
      email,
      avatarUrl,
      organization,
      userNodes: { pageInfo: { total = 0 } = [] } = [],
    },
  } = data;

  const orgName = organization?.name;

  let documentCountDescription = pluralize(total, t('profile-screen.profile-hero.document'));

  documentCountDescription = isCurrentUser
    ? documentCountDescription
    : t('profile-screen.profile-hero.in-common');

  const handleUserTitleUpdate = () => {
    const formattedCurrentTitle = currentTitle.trim();
    if (formattedCurrentTitle && originalTitle !== formattedCurrentTitle) {
      updateJobTitle({ variables: { jobTitle: formattedCurrentTitle } }).then((data: any) => {
        const {
          data: {
            updateMe: { error, ok },
          },
        } = data;

        if (error) {
          // TODO Add error logging
          Logger.log(error);
        }

        if (ok) {
          setOriginalTitle(formattedCurrentTitle);
          setCurrentTitle(formattedCurrentTitle);
          trackingProfileEditFinish(name);
        }
      });
    } else {
      setCurrentTitle(originalTitle);
    }
    setIsEditing(false);
  };

  return (
    <div className={classNames(className, styles.container)}>
      {canEditJobTitle && !isEditing && (
        <div className={styles.edit}>
          <div
            className={classNames('hasTooltip tooltipBelowRight', styles.icon)}
            data-tooltip="Edit Profile"
            onClick={() => {
              setIsEditing(true);
              trackingProfileEditStart(name);
            }}
          >
            <Icon.Edit className={styles.icon} />
          </div>
        </div>
      )}

      <CollaboratorAvatar
        collaborator={{
          displayName: name ? name : email,
          emailAddress: email,
          photoURL: avatarUrl,
        }}
        size="Enormous"
      />

      <div className={styles.meta}>
        <div className={styles.name} title={email}>
          {name ? name : email}
        </div>
        {canSeeJobTitle && (currentTitle !== '' || originalTitle !== '') && (
          <>
            {canEditJobTitle && isEditing ? (
              <>
                <div className={classNames(styles.title, styles.isEditing)}>
                  {/* 
                  //@ts-ignore */}
                  <TextInput
                    className={styles.input}
                    autoFocus
                    value={currentTitle}
                    onChange={(title: any) => {
                      setCurrentTitle(title);
                    }}
                    placeholder={t('profile-screen.profile-hero.job-title-placeholder')}
                    theme="Word"
                  />
                  <span>
                    {t('profile-screen.profile-hero.at')} {toTitleCase(orgName)}
                  </span>
                  <span className={styles.buttonGroup}>
                    <Button
                      size="Tiny"
                      type="Cancel"
                      onClick={() => {
                        setIsEditing(false);
                        trackingProfileEditFinish(name);
                      }}
                    >
                      {t('profile-screen.profile-hero.cancel')}
                    </Button>
                    <Button
                      size="Tiny"
                      onClick={() => {
                        handleUserTitleUpdate();
                        setIsEditing(false);
                      }}
                    >
                      {t('profile-screen.profile-hero.save')}
                    </Button>
                  </span>
                </div>
              </>
            ) : (
              <div className={styles.title}>
                {currentTitle} {t('profile-screen.profile-hero.at')} {toTitleCase(orgName)}
              </div>
            )}
          </>
        )}

        <div className={styles.collabDocs}>
          <div className={styles.documents}>
            <strong>{total.toLocaleString()}</strong> {documentCountDescription}
          </div>
          <div className={styles.created}>
            <strong>{data.profileByIdentifier.ownsNodes.pageInfo.total.toLocaleString()}</strong> <span>{t('profile-screen.profile-hero.created')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
